import type { ReactElement } from 'react'
import { CreateEndpointModal } from '../CreateEndpointModal'
import { JsonImportModal } from '../JsonImportModal'
import { EditEndpointModal } from '../EditEndpointModal'
import { CsvImportModal } from '../CsvImportModal'
import { ImportModal } from '../../../../components/infrastructure/ImportModal'
import { RelationshipImportModal } from '../../../../components/infrastructure/RelationshipImportModal'
import type { CaseInfrastructureTabModalsProps } from '../CaseInfrastructureTabModals'

type EndpointModalsProps = Pick<
  CaseInfrastructureTabModalsProps,
  | 'caseId'
  | 'endpoints'
  | 'existingCustomRoles'
  | 'creating'
  | 'importing'
  | 'mutationError'
  | 'showCreateModal'
  | 'setShowCreateModal'
  | 'editingEndpoint'
  | 'setEditingEndpoint'
  | 'showCsvImportModal'
  | 'setShowCsvImportModal'
  | 'showJsonImportModal'
  | 'setShowJsonImportModal'
  | 'showNewImportModal'
  | 'setShowNewImportModal'
  | 'showRelationshipImportModal'
  | 'setShowRelationshipImportModal'
  | 'handleCreate'
  | 'handleUpdate'
  | 'handleImport'
  | 'refetch'
  | 'clearError'
>

type EndpointEditModalsProps = Pick<
  EndpointModalsProps,
  | 'existingCustomRoles'
  | 'creating'
  | 'mutationError'
  | 'showCreateModal'
  | 'setShowCreateModal'
  | 'editingEndpoint'
  | 'setEditingEndpoint'
  | 'handleCreate'
  | 'handleUpdate'
  | 'clearError'
>

function EndpointEditModals(props: EndpointEditModalsProps): ReactElement {
  const {
    existingCustomRoles,
    creating,
    mutationError,
    showCreateModal,
    setShowCreateModal,
    editingEndpoint,
    setEditingEndpoint,
    handleCreate,
    handleUpdate,
    clearError,
  } = props
  return (
    <>
      {showCreateModal && (
        <CreateEndpointModal
          onClose={() => {
            setShowCreateModal(false)
            clearError()
          }}
          onCreate={(...args: Parameters<typeof handleCreate>) => {
            void handleCreate(...args)
          }}
          creating={creating}
          error={mutationError}
          existingCustomRoles={existingCustomRoles}
        />
      )}
      {editingEndpoint && (
        <EditEndpointModal
          endpoint={editingEndpoint}
          onClose={() => {
            setEditingEndpoint(null)
            clearError()
          }}
          onUpdate={(...args: Parameters<typeof handleUpdate>) => {
            void handleUpdate(...args)
          }}
          updating={creating}
          existingCustomRoles={existingCustomRoles}
          error={mutationError}
        />
      )}
    </>
  )
}

type EndpointImportModalsProps = Pick<
  EndpointModalsProps,
  | 'caseId'
  | 'importing'
  | 'mutationError'
  | 'showCsvImportModal'
  | 'setShowCsvImportModal'
  | 'showJsonImportModal'
  | 'setShowJsonImportModal'
  | 'showNewImportModal'
  | 'setShowNewImportModal'
  | 'showRelationshipImportModal'
  | 'setShowRelationshipImportModal'
  | 'handleImport'
  | 'refetch'
>

function EndpointImportModals(props: EndpointImportModalsProps): ReactElement {
  const {
    caseId,
    importing,
    mutationError,
    showCsvImportModal,
    setShowCsvImportModal,
    showJsonImportModal,
    setShowJsonImportModal,
    showNewImportModal,
    setShowNewImportModal,
    showRelationshipImportModal,
    setShowRelationshipImportModal,
    handleImport,
    refetch,
  } = props
  return (
    <>
      {showCsvImportModal && (
        <CsvImportModal
          caseId={caseId}
          onClose={() => {
            setShowCsvImportModal(false)
            void refetch()
          }}
          onImport={handleImport}
          importing={importing}
          apiError={mutationError}
        />
      )}
      {showJsonImportModal && (
        <JsonImportModal
          onClose={() => {
            setShowJsonImportModal(false)
            void refetch()
          }}
          onImport={handleImport}
          importing={importing}
          apiError={mutationError}
        />
      )}
      {showNewImportModal && (
        <ImportModal
          caseId={caseId}
          isOpen={showNewImportModal}
          onClose={() => {
            setShowNewImportModal(false)
          }}
          onSuccess={() => {
            setShowNewImportModal(false)
            void refetch()
          }}
        />
      )}
      {showRelationshipImportModal && (
        <RelationshipImportModal
          caseId={caseId}
          isOpen={showRelationshipImportModal}
          onClose={() => {
            setShowRelationshipImportModal(false)
          }}
          onSuccess={() => {
            setShowRelationshipImportModal(false)
            void refetch()
          }}
        />
      )}
    </>
  )
}

export function EndpointModalsGroup(props: EndpointModalsProps): ReactElement {
  return (
    <>
      <EndpointEditModals
        existingCustomRoles={props.existingCustomRoles}
        creating={props.creating}
        mutationError={props.mutationError}
        showCreateModal={props.showCreateModal}
        setShowCreateModal={props.setShowCreateModal}
        editingEndpoint={props.editingEndpoint}
        setEditingEndpoint={props.setEditingEndpoint}
        handleCreate={props.handleCreate}
        handleUpdate={props.handleUpdate}
        clearError={props.clearError}
      />
      <EndpointImportModals
        caseId={props.caseId}
        importing={props.importing}
        mutationError={props.mutationError}
        showCsvImportModal={props.showCsvImportModal}
        setShowCsvImportModal={props.setShowCsvImportModal}
        showJsonImportModal={props.showJsonImportModal}
        setShowJsonImportModal={props.setShowJsonImportModal}
        showNewImportModal={props.showNewImportModal}
        setShowNewImportModal={props.setShowNewImportModal}
        showRelationshipImportModal={props.showRelationshipImportModal}
        setShowRelationshipImportModal={props.setShowRelationshipImportModal}
        handleImport={props.handleImport}
        refetch={props.refetch}
      />
    </>
  )
}
